// MaintenanceItem — an org-owned catalog of periodic facility-maintenance
// items (extintores, cortinas eléctricas, aire acondicionado, certificación
// eléctrica, ...), distinct from checklist_templates (ADR-0068/0079/0081)
// and never bound to an acta (ADR-0083). Exists because a checklist
// template row means "a bloque/item subtree an acta walks" — forcing one
// template per distinct maintenance cadence, which in turn forces one acta
// per cadence class for a single physical visit (ADR-0083's forcing case).
// A maintenance item needs none of that: a label, a fixed cadence, an
// applicability scope, and a service-date history. It does not need
// bloques, items, an acta, a folio or a signature.
//
// Unlike checklist_templates.cadenceDays (nullable — an opt-out from a
// freshness pill on an entity that has other reasons to exist),
// cadenceDays here is NOT NULL: a maintenance item's entire reason to
// exist is a fixed periodic cycle (ADR-0083 Decision 2, confirmed by the
// founder's own example — "los tres meses son fijos").
//
// `brandId` has no branded id type of its own, matching
// checklist-template.ts's identical note.
import { isoDateTimeSchema, uuidSchema } from "@bitacorapro/shared";
import { z } from "zod";
import { maintenanceItemIdSchema, organizationIdSchema } from "../ids.js";
// Same shape and same "never derived from junction row count" rule as
// checklistTemplateScopeSchema (ADR-0079 Decision 1's reasoning, reused
// verbatim by ADR-0083 Decision 3) — a separate schema rather than an
// import so the two entities don't share a type that would make a future
// divergence (e.g. a third scope value on one but not the other) a type
// error instead of an explicit decision.
export const maintenanceItemScopeSchema = z.enum(["brand", "assets"]);
export const maintenanceItemSchema = z.object({
    id: maintenanceItemIdSchema,
    organizationId: organizationIdSchema,
    brandId: uuidSchema.nullable(),
    label: z.string().trim().min(1).max(200),
    /** Fixed review cycle in days — e.g. 90 for "cortinas eléctricas,
     *  revisión trimestral". Same for every store that carries this item
     *  (ADR-0083 Decision 2); per-store variance lives in the service
     *  dates, never in the cadence. */
    cadenceDays: z.number().int().positive(),
    displayOrder: z.number().int(),
    active: z.boolean(),
    scope: maintenanceItemScopeSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
//# sourceMappingURL=maintenance-item.js.map